import {Database, useRpcQuery} from '@tnc/shared';
import {useEffect, useState} from 'react';

import {profileDetailsDataItemT} from '@/lib/types/form.types';

import {dateFormatter, defaultDeleteData, usdFormatter} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {DistributorDetailsMarkets} from '@/pages/(shared)/distributor/detail/components/distributorDetailMarkets';
import {useDistributor} from '@/pages/(shared)/distributor/detail/providers/distributorProvider';

import {ConfirmDeleteModalComponent, ProfileCardComponent, ProfileDetailsTabListComponent} from '@/components';
import Page from '@/components/Page';

import {DistributorDetailBrands} from './distributorDetailBrands';
import {DistributorDetailInventory} from './distributorDetailInventory';

/**
 * This component is used to display the distributor details page.
 * @param {SpeculatorProps} props - The props for the component.
 * @returns {JSX.Element} - The distributor details page element.
 */
function DistributorDetails() {
  const {distributor} = useDistributor();

  // State
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);
  const [confirmDeleteError, setConfirmDeleteError] = useState<string | null>(null);

  /**
   * Get order data
   */
  const {data: orderData, refetch: getOrders} = useRpcQuery({
    functionName: 'distributor_order_summary',
    functionArgs: {distributor_id_input: distributor?.distributor_id ?? 0},
    sortOption: {column: 'order_date', direction: 'desc', label: 'Order Date'},
    enabled: !!distributor,
  });

  const handleCancelOrder = async (orderId: number, reason: string) => {
    try {
      const deleteData = await defaultDeleteData();

      const {error} = await SupabaseInstance.supabase
        .from('Order')
        .update({
          order_cancel_reason: reason,
          ...deleteData,
        })
        .eq('order_id', orderId)
        .select('*')
        .single();

      if (error) {
        console.error('Error cancelling order:', error.message);
        throw error;
      }

      setDeleteItemId(null);
      setIsDeleteModalVisible(false);
      getOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw error;
    }
  };

  /**
   * If a distributor is found, we fetch and filter the orders data for the distributor and set it in the state
   */
  useEffect(() => {
    if (!distributor) return;
    getOrders();
  }, [distributor, getOrders]);

  const headerInfo = {
    subHeader: distributor?.distributor_license_number || '',
    perks: [{icon: 'ri-building-line', content: distributor?.distributor_address || ''}],
  };

  const sections = [
    {
      title: 'Distributor Information',
      items: [{header: 'Location', content: `${distributor?.distributor_city}, ${distributor?.distributor_state}`}],
    },
    {
      title: 'Market Information',
      items: [],
    },
  ];

  const tabs = [
    {
      id: 'inventory',
      title: 'Inventory',
      icon: 'ri-stack-line',
      content: <DistributorDetailInventory />,
    },
    {
      id: 'requests',
      title: 'Requests',
      icon: 'ri-file-list-3-line',
      content: <></>,
    },
    {
      id: 'returns',
      title: 'Returns',
      icon: 'ri-refund-2-line',
      content: <></>,
    },
    {
      id: 'markets',
      title: 'Markets',
      icon: 'ri-store-3-line',
      content: <DistributorDetailsMarkets />,
    },
    {
      id: 'brands',
      title: 'Brands',
      icon: 'ri-store-2-line',
      content: <DistributorDetailBrands />,
    },
  ];

  const processedDataItems: profileDetailsDataItemT[] = [
    {
      type: 'generic',
      title: 'Pending Orders',
      data:
        orderData?.data
          .filter(
            (order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) =>
              order.order_group === 'pending'
          )
          .map((order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) => ({
            item: `Order #${order.order_id}`,
            texts: [
              {text1: 'Order Date:', text2: dateFormatter(order.order_date, 'short', true)},
              {text1: 'Type:', text2: order.order_type},
              {text1: 'Order Status:', text2: order.order_status},
              {text1: 'Order Balance:', text2: usdFormatter.format(order.order_balance)},
            ],
          })) || [],
      dataActions: [
        {
          icon: 'ri-delete-bin-line',
          text: '',
          handler: (item: unknown) => {
            setIsDeleteModalVisible(true);
            setDeleteItemId(
              (item as Database['public']['Functions']['distributor_order_summary']['Returns'][0]).order_id
            );
          },
        },
      ],
    },
    {
      type: 'generic',
      title: 'In Progress Orders',
      data:
        orderData?.data
          .filter(
            (order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) =>
              order.order_group === 'inprogress'
          )
          .map((order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) => ({
            item: `Order #${order.order_id}`,
            texts: [
              {text1: 'Order Date:', text2: dateFormatter(order.order_date, 'short', true)},
              {text1: 'Type:', text2: order.order_type},
              {text1: 'Order Status:', text2: order.order_status},
              {text1: 'Order Balance:', text2: usdFormatter.format(order.order_balance)},
            ],
          })) || [],
    },
    {
      type: 'generic',
      title: 'Accepted/Rejected Orders',
      data:
        orderData?.data
          .filter((order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) =>
            ['accepted', 'rejected'].includes(order.order_group)
          )
          .map((order: Database['public']['Functions']['distributor_order_summary']['Returns'][0]) => ({
            item: `Order #${order.order_id}`,
            texts: [
              {text1: 'Order Date:', text2: dateFormatter(order.order_date, 'short', true)},
              {text1: 'Type:', text2: order.order_type},
              {text1: 'Order Status:', text2: order.order_status},
              {text1: 'Order Balance:', text2: usdFormatter.format(order.order_balance)},
            ],
          })) || [],
    },
  ];

  return (
    <Page currentPage="Distributors">
      <div className="grid grid-cols-12 gap-x-6">
        <ProfileCardComponent
          name={distributor?.distributor_name || ''}
          headerInfo={headerInfo}
          sections={sections}
          status={distributor?.distributor_status}
        ></ProfileCardComponent>
        <ProfileDetailsTabListComponent tabs={tabs} dataItems={processedDataItems}></ProfileDetailsTabListComponent>
        <ConfirmDeleteModalComponent
          isVisible={isDeleteModalVisible && deleteItemId !== null}
          description="Are you sure you want to delete this order?"
          title="Delete Order"
          onClose={() => {
            setIsDeleteModalVisible(false);
            setDeleteItemId(null);
          }}
          onConfirm={reason => {
            // TODO: Add validation to throw error if deleteItemId is null or reason is undefined
            if (deleteItemId === null || reason === undefined) {
              setConfirmDeleteError('Please provide a reason for deleting the order');
              return;
            }
            handleCancelOrder(deleteItemId, reason);
          }}
          error={confirmDeleteError}
          hasReason={true}
        />
      </div>
    </Page>
  );
}

export default DistributorDetails;
